import {
  CaseSensitive,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Search,
  Send,
  UserPlus,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PERMISSIONS } from "@/auth/permissions";
import { AddClientModal } from "@/components/clients/AddClientModal";
import { ClientPersonModal } from "@/components/clients/ClientPersonModal";
import { ConsentRequestModal } from "@/components/clients/ConsentRequestModal";
import { DuplicateClientsPanel } from "@/components/clients/DuplicateClientsPanel";
import { NameCasingModal } from "@/components/clients/NameCasingModal";
import {
  ConsentBadges,
  RodoVerifiedMark,
} from "@/components/clients/RodoStatusBadges";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Spinner,
  Tabs,
} from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAgentsForSelect } from "@/hooks/useAgents";
import { useUser } from "@/hooks/useAuth";
import {
  useClients,
  useConsentChannels,
  useDuplicateClients,
  useRequestConsent,
  useRevealSensitiveData,
} from "@/hooks/useClients";
import { useHasPermission } from "@/hooks/usePermission";
import type {
  ClientListItem,
  ClientSensitiveData,
  ClientsQueryParams,
  RodoFilter,
} from "@/types/clients";

const RODO_OPTIONS = [
  { value: "all", label: "Wszyscy klienci" },
  { value: "verified", label: "RODO zweryfikowane" },
  { value: "unverified", label: "Bez weryfikacji RODO" },
];

/** Revealed data disappears on its own — it should not linger on an unattended screen. */
const REVEAL_SECONDS = 30;

interface OpenRequest {
  requestId: string;
  clientName: string;
}

export function ClientsPage() {
  const user = useUser();
  const isAdmin = user?.role === "Administrator";
  const canCreate = useHasPermission(PERMISSIONS.CLIENTS.CREATE);
  // Merging rewrites a client record — the same permission as editing one.
  const canMerge = useHasPermission(PERMISSIONS.CLIENTS.UPDATE);
  const [tab, setTab] = useState<"list" | "duplicates">("list");
  const [casingOpen, setCasingOpen] = useState(false);

  const [params, setParams] = useState<ClientsQueryParams>({
    page: 1,
    pageSize: 20,
    search: "",
    rodo: "all",
  });
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => {
      setParams((prev) => ({ ...prev, search: searchInput.trim(), page: 1 }));
    }, 300);
    return () => {
      window.clearTimeout(id);
    };
  }, [searchInput]);

  const { data, isLoading, isFetching } = useClients(params);
  const { data: duplicates } = useDuplicateClients(params.agentId);
  const { data: agents = [] } = useAgentsForSelect();
  const agentOptions = [
    { value: "", label: "Wszyscy agenci" },
    ...agents.map((a) => ({ value: a.id, label: a.fullName })),
  ];
  const requestConsent = useRequestConsent();
  const { data: channels } = useConsentChannels();
  const smsAvailable = channels?.sms ?? false;

  const [addOpen, setAddOpen] = useState(false);
  const [openRequest, setOpenRequest] = useState<OpenRequest | null>(null);
  const [personClientId, setPersonClientId] = useState<string | null>(null);

  const reveal = useRevealSensitiveData();
  const [revealed, setRevealed] = useState<Record<string, ClientSensitiveData>>(
    {},
  );
  const hideTimers = useRef<Record<string, number>>({});
  useEffect(() => {
    const timers = hideTimers.current;
    return () => {
      for (const id of Object.values(timers)) {
        window.clearTimeout(id);
      }
    };
  }, []);

  function hideSensitive(clientId: string) {
    window.clearTimeout(hideTimers.current[clientId]);
    setRevealed(({ [clientId]: _, ...rest }) => rest);
  }

  function toggleSensitive(client: ClientListItem) {
    if (revealed[client.id]) {
      hideSensitive(client.id);
      return;
    }
    reveal.mutate(client.id, {
      onSuccess: (data) => {
        setRevealed((prev) => ({ ...prev, [client.id]: data }));
        hideTimers.current[client.id] = window.setTimeout(() => {
          hideSensitive(client.id);
        }, REVEAL_SECONDS * 1000);
      },
    });
  }

  function clientName(c: ClientListItem) {
    return `${c.firstName} ${c.lastName}`;
  }

  function handleSendConsent(client: ClientListItem) {
    requestConsent.mutate(
      { clientId: client.id, includesHealth: client.rodo.health !== null },
      {
        onSuccess: (request) => {
          setOpenRequest({
            requestId: request.id,
            clientName: clientName(client),
          });
        },
      },
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      <PageHeader
        title="Klienci"
        description="Dodawaj klientów i zbieraj zgody RODO elektronicznie — kodem SMS lub przez link"
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <Tabs
          tabs={[
            {
              id: "list",
              label: "Lista klientów",
              icon: <Users size={16} aria-hidden />,
            },
            {
              id: "duplicates",
              label: `Możliwe duplikaty${duplicates?.length ? ` (${duplicates.length})` : ""}`,
              icon: <Copy size={16} aria-hidden />,
            },
          ]}
          activeTab={tab}
          onChange={(id) => {
            setTab(id as "list" | "duplicates");
          }}
        />
        {isAdmin && (
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<CaseSensitive size={16} aria-hidden />}
            onClick={() => {
              setCasingOpen(true);
            }}
          >
            Popraw wielkość liter
          </Button>
        )}
      </div>

      {tab === "duplicates" ? (
        <DuplicateClientsPanel
          agentId={params.agentId}
          isAdmin={isAdmin}
          canMerge={canMerge}
        />
      ) : (
        <>
          <Card padding="sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex-1">
                <Input
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                  }}
                  placeholder="Szukaj: nazwisko (lub jego część), telefon, e-mail, pełny PESEL..."
                  leftIcon={<Search size={16} />}
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {isAdmin && (
                  <Select
                    options={agentOptions}
                    value={params.agentId ?? ""}
                    onChange={(val) => {
                      setParams((prev) => ({
                        ...prev,
                        agentId: val || undefined,
                        page: 1,
                      }));
                    }}
                    className="w-full sm:w-52"
                  />
                )}
                <Select
                  options={RODO_OPTIONS}
                  value={params.rodo ?? "all"}
                  onChange={(val) => {
                    setParams((prev) => ({
                      ...prev,
                      rodo: val as RodoFilter,
                      page: 1,
                    }));
                  }}
                  className="w-full sm:w-56"
                />
                {canCreate && (
                  <Button
                    leftIcon={<UserPlus size={16} />}
                    onClick={() => {
                      setAddOpen(true);
                    }}
                    className="w-full sm:w-auto"
                  >
                    Dodaj klienta
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {reveal.isError && (
            <Alert variant="error">
              {(reveal.error as { message?: string }).message ??
                "Nie udało się pokazać danych"}
            </Alert>
          )}

          {requestConsent.isError && (
            <Alert variant="error">
              {(requestConsent.error as { message?: string }).message ??
                "Nie udało się wysłać wniosku"}
            </Alert>
          )}

          <Card padding="none">
            {isLoading ? (
              <div className="flex justify-center py-24">
                <Spinner size="lg" />
              </div>
            ) : data && data.items.length > 0 ? (
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3">
                    <Spinner />
                  </div>
                )}
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-edge bg-subtle">
                      {[
                        "Klient",
                        "Kontakt",
                        ...(isAdmin ? ["Agent"] : []),
                        "Status RODO",
                        "Zgody",
                        "",
                      ].map((h) => (
                        <th
                          key={h || "actions"}
                          className="px-4 py-3 text-left text-xs font-medium text-dim"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-edge-soft">
                    {data.items.map((c) => {
                      const sensitive = revealed[c.id];
                      return (
                        <tr
                          key={c.id}
                          className="transition-colors hover:bg-subtle"
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-main">
                              {clientName(c)}
                            </p>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => {
                                  setPersonClientId(c.id);
                                }}
                                className="my-0.5 rounded bg-adara-50 px-1.5 py-0.5 text-xs font-medium text-adara-700 hover:bg-adara-100 dark:bg-adara-900/30 dark:text-adara-300"
                                title="Pokaż tę osobę u wszystkich agentów"
                              >
                                {c.agentCount > 1
                                  ? `u ${c.agentCount} agentów`
                                  : "agenci: 1 · dodaj"}
                              </button>
                            )}
                            <p className="text-xs text-faint">
                              PESEL{" "}
                              <span
                                className={
                                  sensitive ? "font-mono text-main" : ""
                                }
                              >
                                {sensitive?.pesel ?? c.peselMasked}
                              </span>
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-sub">
                            <div className="flex items-start gap-2">
                              <div className={sensitive ? "text-main" : ""}>
                                <p>{sensitive?.phone ?? c.phoneMasked}</p>
                                {(sensitive?.email ?? c.emailMasked) && (
                                  <p className="text-xs">
                                    {sensitive?.email ?? c.emailMasked}
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  toggleSensitive(c);
                                }}
                                disabled={
                                  reveal.isPending && reveal.variables === c.id
                                }
                                className="mt-0.5 rounded p-1 text-faint hover:bg-muted hover:text-main disabled:opacity-50"
                                title={
                                  sensitive
                                    ? "Ukryj dane wrażliwe"
                                    : `Pokaż dane wrażliwe (PESEL, telefon, e-mail) — na ${REVEAL_SECONDS} s, odnotowane w dzienniku`
                                }
                                aria-label={
                                  sensitive
                                    ? "Ukryj dane wrażliwe"
                                    : "Pokaż dane wrażliwe"
                                }
                              >
                                {sensitive ? (
                                  <EyeOff size={16} aria-hidden />
                                ) : (
                                  <Eye size={16} aria-hidden />
                                )}
                              </button>
                            </div>
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-3 text-sm text-sub">
                              {c.agentName}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <RodoVerifiedMark
                              rodo={c.rodo}
                              canDownloadScan={isAdmin}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <ConsentBadges rodo={c.rodo} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end gap-1.5">
                              {c.pendingRequestId ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  leftIcon={<KeyRound size={14} aria-hidden />}
                                  onClick={() => {
                                    setOpenRequest({
                                      requestId: c.pendingRequestId ?? "",
                                      clientName: clientName(c),
                                    });
                                  }}
                                >
                                  Wpisz kody
                                </Button>
                              ) : (
                                canCreate && (
                                  <Button
                                    size="sm"
                                    variant={
                                      c.rodo.verified ? "ghost" : "outline"
                                    }
                                    leftIcon={<Send size={14} aria-hidden />}
                                    isLoading={
                                      requestConsent.isPending &&
                                      requestConsent.variables.clientId === c.id
                                    }
                                    disabled={!c.emailMasked && !smsAvailable}
                                    title={
                                      !c.emailMasked && !smsAvailable
                                        ? "Klient nie ma e-maila, a wysyłka SMS jeszcze nie działa"
                                        : undefined
                                    }
                                    onClick={() => {
                                      handleSendConsent(c);
                                    }}
                                  >
                                    {c.rodo.verified
                                      ? "Odnów zgodę"
                                      : c.emailMasked
                                        ? "Wyślij e-mail"
                                        : "Wyślij SMS"}
                                  </Button>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {data.totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-edge-soft px-4 py-3 text-sm text-sub">
                    <span>
                      Strona {data.page} z {data.totalPages} · {data.totalCount}{" "}
                      klientów
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={data.page <= 1}
                        onClick={() => {
                          setParams((prev) => ({
                            ...prev,
                            page: prev.page - 1,
                          }));
                        }}
                      >
                        Poprzednia
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={data.page >= data.totalPages}
                        onClick={() => {
                          setParams((prev) => ({
                            ...prev,
                            page: prev.page + 1,
                          }));
                        }}
                      >
                        Następna
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState
                icon={<UserRound size={32} />}
                title="Brak klientów"
                description={
                  params.search || params.rodo !== "all"
                    ? "Nie znaleziono klientów pasujących do filtrów."
                    : "Dodaj pierwszego klienta, aby wysłać mu zgodę RODO."
                }
              />
            )}
          </Card>
        </>
      )}

      <AddClientModal
        isOpen={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onRequestSent={(request, name) => {
          setOpenRequest({ requestId: request.id, clientName: name });
        }}
      />
      <NameCasingModal
        isOpen={casingOpen}
        onClose={() => {
          setCasingOpen(false);
        }}
      />
      <ClientPersonModal
        clientId={personClientId}
        onClose={() => {
          setPersonClientId(null);
        }}
      />
      <ConsentRequestModal
        requestId={openRequest?.requestId ?? null}
        clientName={openRequest?.clientName ?? ""}
        onClose={() => {
          setOpenRequest(null);
        }}
      />
    </div>
  );
}
